import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, Typography } from '@pulse/ui';
import type { ComponentProps, MouseEvent, ReactNode, UIEvent } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

type TypographyPropsLite = ComponentProps<typeof Typography>;
type BoxPropsLite = ComponentProps<typeof Box>;

export interface ScrollableContainerProps {
  children: ReactNode;
  /** CSS max-height of the scroll viewport, e.g. `120` (px) or `'9em'`. */
  maxHeight: number | string;
  sx?: BoxPropsLite['sx'];
}

// Shared scroll-viewport chrome behind both ScrollableText (plain text) and ScrollableBox
// (arbitrary children, e.g. rendered markdown) — overflow detection, click-to-scroll chevrons,
// and the scroll container itself all live here so the two stay visually/behaviorally identical.
function ScrollableContainer({ children, maxHeight, sx }: ScrollableContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  // Re-derives overflow straight from the scroll container itself (scrollHeight vs clientHeight
  // — no horizontal check needed, `whiteSpace: pre-wrap` + a fixed-width container mean this box
  // can only ever overflow vertically) rather than pulling in TruncatableText's isTruncated,
  // which exists for a different job (gating a hover tooltip) and checks width too. Re-runs on
  // `children`/`maxHeight` change in case content changes without the box's own size changing
  // (which the ResizeObserver alone wouldn't catch), and stays subscribed for any actual resize.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const overflowing = el.scrollHeight > el.clientHeight;
      setIsScrollable(overflowing);
      setAtTop(el.scrollTop <= 0);
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children, maxHeight]);

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    setAtTop(el.scrollTop <= 0);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  }

  // Chevrons double as click-to-scroll buttons, not just a passive affordance — step by ~2 lines
  // per click (smooth) rather than jumping straight to top/bottom, so repeated clicks read as
  // incremental scrolling. `stopPropagation` guards against a parent card that's itself
  // clickable (e.g. a card whose Paper has its own onClick).
  function scrollByStep(direction: 'up' | 'down', e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    ref.current?.scrollBy({ top: direction === 'down' ? 48 : -48, behavior: 'smooth' });
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={ref}
        onScroll={handleScroll}
        sx={{
          maxHeight,
          overflowY: 'auto',
          pr: 2.5,
          ...sx,
        }}
      >
        {children}
      </Box>
      {isScrollable && !atTop && (
        <IconButton
          size="small"
          aria-label="Scroll up"
          onClick={(e) => scrollByStep('up', e)}
          sx={{ position: 'absolute', top: -4, right: -4, p: 0.25, color: 'text.disabled', bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      {isScrollable && !atBottom && (
        <IconButton
          size="small"
          aria-label="Scroll down"
          onClick={(e) => scrollByStep('down', e)}
          sx={{ position: 'absolute', bottom: -4, right: -4, p: 0.25, color: 'text.disabled', bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

export interface ScrollableTextProps {
  text: string;
  /** Visible line count before the box scrolls instead of growing. Default 6. */
  maxLines?: number;
  sx?: TypographyPropsLite['sx'];
  variant?: TypographyPropsLite['variant'];
  color?: TypographyPropsLite['color'];
  fontWeight?: TypographyPropsLite['fontWeight'];
}

// Fixed-height text that scrolls internally instead of truncating — for a mobile card field
// (no hover available for TruncatableText's tooltip reveal) where the full value should stay
// reachable without leaving the card. Up/down chevrons double as click-to-scroll buttons: each
// shown only while there's more content in that direction, both hidden once the text fits
// within `maxLines` to begin with.
export function ScrollableText({ text, maxLines = 6, sx, variant, color, fontWeight }: ScrollableTextProps) {
  return (
    <ScrollableContainer maxHeight={`${maxLines * 1.5}em`}>
      <Typography
        component="div"
        variant={variant}
        color={color}
        fontWeight={fontWeight}
        sx={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          lineHeight: '1.5em',
          ...sx,
        }}
      >
        {text}
      </Typography>
    </ScrollableContainer>
  );
}

export type ScrollableBoxProps = Omit<ScrollableContainerProps, 'maxHeight'> & {
  /** CSS max-height of the scroll viewport. Default 120px. */
  maxHeight?: ScrollableContainerProps['maxHeight'];
};

// Same fixed-height/click-to-scroll-chevron affordance as ScrollableText, but for arbitrary
// rendered content (e.g. a read-only MarkdownField) rather than a plain string — use this where
// the content needs its own markup/formatting instead of a Typography wrapper.
export function ScrollableBox({ children, maxHeight = 120, sx }: ScrollableBoxProps) {
  return (
    <ScrollableContainer maxHeight={maxHeight} sx={sx}>
      {children}
    </ScrollableContainer>
  );
}
